/* eslint-disable react/no-array-index-key */
import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { defineMessages, FormattedMessage } from 'react-intl';
import {
  Avatar,
  FormControl, FormHelperText,
  InputLabel,
  ListItem, ListItemText,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';
import _ from 'lodash';

import * as actions from '../../../../state';
import styles from '../styles.module.css';
import ColumnList from './common-controls/ColumnList';
import { AutoComplete } from '../../../common';
import createChartNameFromSources from '../../../../utils/createChartNameFromSources';

const messages = defineMessages({
  FILTER_DATA: {
    id: 'FILTER_DATA',
    defaultMessage: 'Filter',
  },
  FILTER_DATA_HELPTEXT: {
    id: 'FILTER_DATA_HELPTEXT',
    defaultMessage: 'Leave filter empty for all data.',
  },
  FILTER_DATA_PLACEHOLDER: {
    id: 'FILTER_DATA_PLACEHOLDER',
    defaultMessage: 'Select filter(s)...',
  },
  FILTER_DATA_SINGULAR_PLACEHOLDER: {
    id: 'FILTER_BY_QUEUE_SINGULAR_PLACEHOLDER',
    defaultMessage: 'Select filter...',
  },
  FILTER_DATA_SOURCE_HELPTEXT: {
    id: 'FILTER_DATA_SOURCE_HELPTEXT',
    defaultMessage: 'Filter your selected data set.',
  },
  FILTER_SINGLE_HELPTEXT: {
    id: 'FILTER_SINGLE_HELPTEXT',
    defaultMessage: 'The selected statistic does not support multiple filters.',
  },
  FILTER_AND_BREAKDOWN_DATA_SOURCE_HELPTEXT: {
    id: 'FILTER_AND_BREAKDOWN_DATA_SOURCE_HELPTEXT',
    defaultMessage: 'Filter and break down your selected data set.',
  },
  TOGGLE_COLUMNS_HELPTEXT: {
    id: 'TOGGLE_COLUMNS_HELPTEXT',
    defaultMessage: 'Select which columns to include on this card.',
  },
  BREAKDOWN_SELECT: {
    id: 'BREAKDOWN_SELECT',
    defaultMessage: 'Breakdown',
  },
  BREAKDOWN_HELPTEXT: {
    id: 'BREAKDOWN_HELPTEXT',
    defaultMessage: 'The breakdown changes how the data is grouped',
  },
  GAUGE_LIMIT: {
    id: 'GAUGE_LIMIT',
    defaultMessage: 'Gauge upper limit',
  },
  GAUGE_LIMIT_HELPTEXT: {
    id: 'GAUGE_LIMIT_HELPTEXT',
    defaultMessage: 'The upper limit which represents a "full" gauge',
  },
  MINUTES: {
    id: 'MINUTES',
    defaultMessage: 'Minutes',
  },
  SECONDS: {
    id: 'SECONDS',
    defaultMessage: 'Seconds',
  },
  AGENTS: {
    id: 'AGENTS',
    defaultMessage: 'Agents',
  },
  CALLS: {
    id: 'CALLS',
    defaultMessage: 'Calls',
  },
  PERCENT: {
    id: 'PERCENT',
    defaultMessage: 'Percent',
  },
  AGGREGATED: {
    id: 'AGGREGATED',
    defaultMessage: 'Aggregate',
  },
  PER_AGENT: {
    id: 'PER_AGENT',
    defaultMessage: 'Per Agent',
  },
  PER_QUEUE: {
    id: 'PER_QUEUE',
    defaultMessage: 'Per Queue',
  },
});

function DataSourceControlItems({
  chartOptions,
  selectedSource,
  statDef,
  tabContent,
  updateSelectedSource,
}) {
  const dispatch = useDispatch();

  const selectedChart = useSelector(actions.readStateSelectedChart);
  const chartsManifest = useSelector(actions.readChartsManifest);
  const choices = useSelector(actions.readStateAutocompleteChoices);

  const handleCategorySelect = (e) => {
    const clonedSource = _.cloneDeep(selectedSource);
    clonedSource.category = e.target.value;
    updateSelectedSource(clonedSource);

    const clonedChart = _.cloneDeep(selectedChart);

    clonedChart.data_sources = selectedChart.data_sources.map((source) => (source.index === selectedSource.index
      ? _.cloneDeep({ ...selectedSource, category: e.target.value }) : source));

    clonedChart.renderRequired = true;
    clonedChart.name = createChartNameFromSources(chartsManifest, clonedChart);
    dispatch(actions.updateSelectedChart(clonedChart));
  };

  const handleColumnToggle = (column) => {
    let selectedColumnsArray = selectedChart?.data_sources[0]?.queryParams?.stats;

    if (selectedColumnsArray.includes(column)) {
      selectedColumnsArray = selectedColumnsArray.filter((colFromArr) => colFromArr !== column);
    } else {
      selectedColumnsArray.push(column);
    }

    const clonedChart = _.cloneDeep(selectedChart);
    clonedChart.data_sources[0].queryParams.stats = selectedColumnsArray;
    clonedChart.renderRequired = true;

    dispatch(actions.updateSelectedChart(clonedChart));
  };

  const updateChartSource = (updatedSource) => {
    const clonedChart = _.cloneDeep(selectedChart);

    clonedChart.data_sources = clonedChart.data_sources.map((source) => (
      source.index === updatedSource.index
        ? _.cloneDeep(updatedSource)
        : source
    ));

    clonedChart.name = createChartNameFromSources(chartsManifest, clonedChart);
    clonedChart.renderRequired = true;

    dispatch(actions.updateSelectedChart(clonedChart));
  };

  const handleOutOfChange = (e) => {
    const clonedDataSource = _.cloneDeep(selectedSource);
    clonedDataSource.queryParams.outOf = e.target.value;
    updateChartSource(clonedDataSource);
  };

  const handleFilterSelect = (selectedOptions) => {
    let options = selectedOptions;
    if (!Array.isArray(selectedOptions) && selectedOptions !== null) {
      options = [selectedOptions];
    }

    const clonedSource = _.cloneDeep(selectedSource);
    clonedSource.filters = _.cloneDeep(options);

    updateSelectedSource(clonedSource);
    updateChartSource(clonedSource);
  };

  const categorySelect = () => (
    <ListItem key="breakdown_1">
      <FormControl style={{ width: '100%' }}>
        <InputLabel shrink htmlFor="age-label-placeholder">
          <FormattedMessage {...messages.BREAKDOWN_SELECT} />
        </InputLabel>
        <Select
          name="breakdown"
          value={selectedSource.category}
          onChange={handleCategorySelect}
          displayEmpty
        >
          {statDef.categoryDefs.map((category, index) => (
            <MenuItem
              key={`category_${index}`}
              value={category.category}
            >
              <FormattedMessage {...messages[category.category]} />
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          <FormattedMessage {...messages.BREAKDOWN_HELPTEXT} />
        </FormHelperText>
      </FormControl>
    </ListItem>
  );

  const filters = selectedSource.filters || [];
  const outOf = selectedSource.queryParams.outOf || 100;
  const togglingCols = tabContent === 'toggleColumns';

  let helptext;
  let secondaryLabel;
  let options;

  if (statDef) {
    options = chartOptions.concat(statDef.chartOptions);
  }

  if (selectedSource.stat !== 'custom' && options.find((chartControl) => chartControl.type === 'queueFilter')) {
    helptext = <FormattedMessage {...messages.FILTER_DATA_SOURCE_HELPTEXT} />;
  }

  if (statDef?.hasCategories) {
    secondaryLabel = <FormattedMessage {...messages[statDef?.unit?.toUpperCase()]} />;
    helptext = <FormattedMessage {...messages.FILTER_AND_BREAKDOWN_DATA_SOURCE_HELPTEXT} />;
  }

  let descriptionItem = (
    <>
      <ListItem key="001_desc_helptext" className={styles.helptext}>
        {helptext}
      </ListItem>

      <ListItem key="001_stat_helptext">
        {selectedSource.stat.length < 4 && (
          <Avatar className={styles.sourceOptionAvatar}>
            {selectedSource.stat}
          </Avatar>
        )}

        <ListItemText
          className={styles.sourceText}
          primary={statDef?.label}
          secondary={secondaryLabel}
        />
      </ListItem>
    </>
  );

  if (togglingCols) {
    descriptionItem = (
      <ListItem key="001_desc_helptext" className={styles.helptext}>
        <FormattedMessage {...messages.TOGGLE_COLUMNS_HELPTEXT} />
      </ListItem>
    );
  }

  const controlItems = options?.map((chartControl, cCID) => {
    if (chartControl.type === 'columnList' && togglingCols && !selectedChart.type.includes('Grid')) { // grids won't have configurable elements in this version
      const selectedColumns = selectedChart.data_sources[0]
        ? selectedChart.data_sources[0].queryParams.stats : [];

      return (
        <ColumnList
          key={cCID}
          selectedColumns={selectedColumns}
          supportedColumns={statDef?.supportedColumns}
          handleColumnToggle={handleColumnToggle}
        />
      );
    }

    if (chartControl.type === 'outOf' && !togglingCols) { // grids won't have configurable elements in this version
      return (
        <ListItem key={cCID}>
          <FormControl style={{ width: '100%' }}>
            <InputLabel shrink>
              <FormattedMessage {...messages.GAUGE_LIMIT} />
            </InputLabel>

            <TextField
              key={cCID}
              value={outOf}
              label={<FormattedMessage {...messages.GAUGE_LIMIT} />}
              onChange={handleOutOfChange}
            />

            <FormHelperText>
              <FormattedMessage {...messages.GAUGE_LIMIT_HELPTEXT} />
            </FormHelperText>
          </FormControl>
        </ListItem>
      );
    }

    if (chartControl.type === 'queueFilter' && !togglingCols && selectedSource.stat !== 'custom') {
      const choicesMatrix = {};

      if (statDef?.categoryDefs) { // this is when needing to filter by Breakdown and stat type
        const currentCatagoryDefFilterObjs = statDef?.categoryDefs?.filter(
          (category) => selectedSource.category === category.category,
        );
        if (statDef?.filterCategories) {
          statDef?.filterCategories?.forEach((filterObj) => {
            currentCatagoryDefFilterObjs[0].filterObjects.forEach((catagoryObj) => {
              if (catagoryObj === filterObj) {
                choicesMatrix[filterObj] = choices[filterObj];
              }
            });
          });
        }
      } else if (statDef?.filterCategories) { // this is when needing to filter and stat type only
        statDef?.filterCategories?.forEach((filterObj) => {
          choicesMatrix[filterObj] = choices[filterObj];
        });
      }

      return (
        <ListItem key={cCID}>
          <FormControl style={{ width: '100%' }}>
            <InputLabel shrink>
              <FormattedMessage {...messages.FILTER_DATA} />
            </InputLabel>

            <div style={{ marginTop: 20, zIndex: 4 }}>
              <AutoComplete
                open
                isGroupedOptions
                isMulti={statDef?.filterMultiples}
                options={choicesMatrix}
                selectedOptions={filters}
                handleAutocomplete={handleFilterSelect}
                placeholder={statDef?.filterMultiples
                  ? <FormattedMessage {...messages.FILTER_DATA_PLACEHOLDER} />
                  : <FormattedMessage {...messages.FILTER_DATA_SINGULAR_PLACEHOLDER} />}
              />
            </div>

            <FormHelperText>
              <FormattedMessage {...messages.FILTER_DATA_HELPTEXT} />
              {!statDef?.filterMultiples && <FormattedMessage {...messages.FILTER_SINGLE_HELPTEXT} />}
            </FormHelperText>
          </FormControl>
        </ListItem>
      );
    }

    return null;
  });

  if (statDef?.categoryDefs) {
    controlItems.unshift(categorySelect());
  }

  return (
    <>
      {descriptionItem}
      {controlItems}
    </>
  );
}

DataSourceControlItems.defaultProps = {
  selectedSource: null,
  statDef: null,
};

DataSourceControlItems.propTypes = {
  chartOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedSource: PropTypes.shape({}),
  statDef: PropTypes.shape({}),
  tabContent: PropTypes.string.isRequired,
  updateSelectedSource: PropTypes.func.isRequired,
};

export default DataSourceControlItems;
